import { useState } from 'preact/hooks';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { SLATE, formatHours, formatDateShort, formatTime } from './shifts-shared.js';
import { CorrectShiftModal } from './CorrectShiftModal.jsx';
import { ClockInEditor } from './ClockInEditor.jsx';

export const ShiftsIndexPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { data: users } = useApi(isAdmin ? '/users' : null);
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [editing, setEditing] = useState(null);

  const params = new URLSearchParams();
  if (isAdmin && userId) params.set('userId', userId);
  if (from) params.set('from', from);
  if (to) params.set('to', `${to} 23:59:59`); // make the end date inclusive of its whole day
  const qs = params.toString();
  const { data: shiftsList, refetch } = useApi(`/shifts${qs ? `?${qs}` : ''}`);

  return (
    <div>
      <section class="mb-5 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: SLATE }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: SLATE }}>Shift Tracker</span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">Shift log</h1>
          <p class="m-0 text-sm text-text-secondary">
            {isAdmin ? "Everyone’s clock in/out history." : 'Your clock in/out history.'}
          </p>
        </div>
      </section>

      <div class="mb-4 flex flex-wrap items-center gap-2">
        {isAdmin && (
          <select
            value={userId}
            onChange={(e) => setUserId(e.currentTarget.value)}
            class="rounded-md border border-border-strong bg-bg-card px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          >
            <option value="">Everyone</option>
            {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
          </select>
        )}
        <input
          type="date"
          value={from}
          onInput={(e) => setFrom(e.currentTarget.value)}
          class="rounded-md border border-border-strong bg-bg-card px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <span class="text-sm text-text-muted">to</span>
        <input
          type="date"
          value={to}
          onInput={(e) => setTo(e.currentTarget.value)}
          class="rounded-md border border-border-strong bg-bg-card px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <span class="ml-auto text-[12px] text-text-tertiary">
          {shiftsList ? `${shiftsList.length} shift${shiftsList.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {!shiftsList && <p class="text-sm text-text-tertiary">Loading…</p>}
      {shiftsList && shiftsList.length === 0 && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No shifts.</p>
        </div>
      )}
      {shiftsList && shiftsList.length > 0 && (
        <div class="space-y-1.5">
          {shiftsList.map((s) => (
            <div
              key={s.id}
              class="relative flex flex-wrap items-center gap-3 overflow-hidden rounded-md border border-border-subtle bg-bg-card p-3"
            >
              <span class="pointer-events-none absolute inset-y-0 left-0 w-[3px]" style={{ background: SLATE }} aria-hidden="true" />
              <div class="w-24 shrink-0">
                <p class="m-0 text-[13px] font-medium text-text-primary">{formatDateShort(s.clockInAt)}</p>
                {isAdmin && <p class="m-0 truncate text-[11px] text-text-tertiary">{s.userName ?? 'Unattributed'}</p>}
              </div>
              <div class="flex items-center gap-2 text-[13px] text-text-body">
                <span>{formatTime(s.clockInAt)}</span>
                <span class="text-text-muted">→</span>
                {s.clockOutAt ? (
                  <span>{formatTime(s.clockOutAt)}</span>
                ) : (
                  <span class="rounded-full border border-status-info/30 bg-status-info/[0.12] px-2 py-[1px] text-[10px] font-semibold uppercase tracking-[0.04em] text-status-info">
                    In progress
                  </span>
                )}
                {!isAdmin && !s.clockOutAt && <ClockInEditor clockInAt={s.clockInAt} onSaved={refetch} />}
              </div>
              <div class="text-[12px] text-text-tertiary">{s.breakMinutes}m break</div>
              {s.notes && <div class="min-w-0 flex-1 truncate text-[12px] text-text-tertiary">{s.notes}</div>}
              <div class="ml-auto flex items-center gap-3">
                <span class="font-serif text-[16px] font-medium text-text-primary">{formatHours(s.totalHours)}</span>
                {isAdmin && (
                  <button onClick={() => setEditing(s)} class="text-[11px] text-text-tertiary hover:text-brand" aria-label="Correct">
                    Correct
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isAdmin && (
        <CorrectShiftModal
          open={!!editing}
          shift={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refetch(); }}
        />
      )}
    </div>
  );
};
